import {Route} from '@angular/router';
import {authMobileRoutes} from '@ew-mobile/auth-core-mobile';
import {appMobileShellRoutes} from '@ew-mobile/core-layout';
import {staffPortalRoutes} from '@ew-mobile/mobile-staff-routes';

/**
 * Mobile Application Root Routes Configuration
 *
 * This configuration defines the top-level routing structure for the Enterwait mobile application.
 * The routes are organized in a hierarchical manner to support the mobile-first architecture
 * with Ionic navigation patterns (tabs, side menus, modal navigation).
 *
 * Route Loading Order:
 * 1. Authentication routes - Public routes for login, registration, password reset
 * 2. Main app shell routes - Protected routes with mobile navigation (tabs/side menu)
 * 3. Staff portal routes - Special routes for staff members using the mobile app
 * 4. Wildcard fallback - 404 page for unmatched routes
 *
 * Mobile-Specific Considerations:
 * - Routes use lazy loading to optimize initial bundle size for mobile devices
 * - Navigation follows Ionic conventions (ion-tabs, ion-nav, ion-router-outlet)
 * - Deep linking support for push notifications and external links
 * - Route guards protect authenticated routes and check user roles
 * - Capacitor URL schemes handled for native app navigation
 *
 * @see authMobileRoutes - Public authentication flows optimized for mobile UX
 * @see appMobileShellRoutes - Main application shell with bottom tabs navigation
 * @see staffPortalRoutes - Staff-specific features and workflows
 */
export const appRoutes: Route[] = [
  // Authentication routes - Public access for login, registration, password recovery
  // Includes mobile-optimized login screens, social auth, and biometric authentication
  ...authMobileRoutes,

  // Main application shell routes - Protected routes requiring authentication
  // Provides the core mobile app experience with bottom tab navigation:
  // - Dashboard tab (home, activity feed)
  // - Explore tab (search services, discover businesses)
  // - Bookings tab (manage reservations, track wait times)
  // - Settings tab (profile, preferences, notifications)
  ...appMobileShellRoutes,

  // Staff portal routes - Special routes for business staff members
  // Allows staff to manage queues, update wait times, and communicate with customers
  // These routes are conditionally displayed based on user role
  ...staffPortalRoutes,

  // Wildcard route - Catches all unmatched paths and displays 404 error page
  // This should always be the last route in the configuration
  {
    path: '**',
    loadComponent: async () => (await import('@ew/page-not-found')).PageNotFoundComponent,
  },
];
